import { useContext, useEffect } from "react";

import { AuthContext } from "../AuthProvider";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";

// Codesmith access is per org, so the cache is keyed by org name to avoid
// leaking one org's flag into another while orgs load.
const CODESMITH_ENABLED_STORAGE_KEY_PREFIX = "sidebar-codesmith-enabled";
// Routes without an org segment (/usage, /settings, /support, /admin, ...)
// have no org to key the lookup with while auth and orgs load, so the last
// resolved org is kept as a pointer for those lookups. The server-side
// active org is what those routes resolve to, and this is the best local
// guess of it.
const LAST_ACTIVE_ORG_STORAGE_KEY = "sidebar-codesmith-last-org";
// The cache belongs to one account: without this marker, a later session on
// the same browser could adopt the previous account's flag on org-less
// routes via the last-org pointer until orgs resolve.
const CACHE_OWNER_STORAGE_KEY = "sidebar-codesmith-user";

function storageKeyForOrg(org: string) {
  return `${CODESMITH_ENABLED_STORAGE_KEY_PREFIX}:${org}`;
}

export function clearCodesmithLayoutCache() {
  if (typeof window === "undefined") return;
  try {
    const doomed = [LAST_ACTIVE_ORG_STORAGE_KEY, CACHE_OWNER_STORAGE_KEY];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(`${CODESMITH_ENABLED_STORAGE_KEY_PREFIX}:`)) {
        doomed.push(key);
      }
    }
    for (const key of doomed) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // Local storage is an optimization; the resolved org setting remains authoritative.
  }
}

/**
 * Last resolved active org, for navigation targets on org-less routes while
 * orgs load. Paired with `getCachedCodesmithEnabled(undefined)`: when the
 * cached flag mounts Codesmith UI before `activeGithubOrg` resolves, this is
 * the org that cached flag belongs to, and org-less paths like
 * `/sessions/new` don't exist (sessions live under `/[org]/sessions`).
 */
export function getCachedLastActiveOrg(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return (
      window.localStorage.getItem(LAST_ACTIVE_ORG_STORAGE_KEY) ?? undefined
    );
  } catch {
    return undefined;
  }
}

export function getCachedCodesmithEnabled(
  org: string | null | undefined,
): boolean | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const effectiveOrg =
      org || window.localStorage.getItem(LAST_ACTIVE_ORG_STORAGE_KEY);
    if (!effectiveOrg) return undefined;
    const value = window.localStorage.getItem(storageKeyForOrg(effectiveOrg));
    return value === null ? undefined : value === "true";
  } catch {
    return undefined;
  }
}

/**
 * Codesmith availability for the active org, falling back to the cached
 * last-known value while the org query is in flight so Codesmith-gated UI
 * (the rail row) doesn't pop in after every load.
 * `undefined` means genuinely unknown (first visit).
 */
export function useCodesmithEnabledWithCache(): boolean | undefined {
  const { user } = useContext(AuthContext);
  const {
    activeGithubOrg,
    codesmithEnabled,
    codesmithEnabledKnown,
    orgsResolved,
  } = useActiveGithubOrgForUserNonSuspense();
  // A successful resolve is authoritative in every case: an org in
  // installations carries its real flag, and a missing one (no installations,
  // or a URL org this account can't see) means codesmithEnabled resolved to
  // false, so no cached previous flag applies. Only query errors (rate limit,
  // SSO, network) stay unresolved, so an unknown state never removes the rail
  // (or poisons the cache) as if Codesmith were resolved off.
  const isUnresolved = !orgsResolved;

  useEffect(() => {
    if (!user) return;
    try {
      if (
        window.localStorage.getItem(CACHE_OWNER_STORAGE_KEY) !== String(user.id)
      ) {
        clearCodesmithLayoutCache();
        window.localStorage.setItem(CACHE_OWNER_STORAGE_KEY, String(user.id));
      }
      if (isUnresolved) return;
      if (!activeGithubOrg) {
        // No installations: the last-org pointer names an org this account
        // can no longer see, so drop it before the next load resurrects
        // Codesmith UI (and navigation targets) from that org's cached flag.
        window.localStorage.removeItem(LAST_ACTIVE_ORG_STORAGE_KEY);
        return;
      }
      if (!codesmithEnabledKnown) {
        // The URL org resolved outside this account's installations: its
        // cached flag is stale (the account can't see the org), and neither
        // caching the fallback false nor repointing the last-org pointer at
        // it would describe an org the server can resolve to. A last-org
        // pointer still naming it is equally stale and would route org-less
        // nav targets to the inaccessible org on a later unresolved load, so
        // it goes too.
        window.localStorage.removeItem(storageKeyForOrg(activeGithubOrg));
        if (
          window.localStorage.getItem(LAST_ACTIVE_ORG_STORAGE_KEY) ===
          activeGithubOrg
        ) {
          window.localStorage.removeItem(LAST_ACTIVE_ORG_STORAGE_KEY);
        }
        return;
      }
      // Superusers cache the resolved value like everyone else, including
      // the forced true from impersonation: the cache is owner-marked to
      // this account, and the next resolve for the same org forces true
      // again, so the cached flag stays self-consistent for this browser.
      window.localStorage.setItem(
        storageKeyForOrg(activeGithubOrg),
        String(codesmithEnabled),
      );
      window.localStorage.setItem(LAST_ACTIVE_ORG_STORAGE_KEY, activeGithubOrg);
    } catch {
      // Local storage is an optimization; the resolved org setting remains authoritative.
    }
  }, [
    user,
    isUnresolved,
    activeGithubOrg,
    codesmithEnabled,
    codesmithEnabledKnown,
  ]);

  // Fall back to the cached value while org data is loading so the Codesmith
  // rail row doesn't pop in or out before the feature query resolves.
  return isUnresolved
    ? getCachedCodesmithEnabled(activeGithubOrg)
    : codesmithEnabled;
}
